import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  apiClient,
  baseURL,
  getCachedSessionUser,
  SESSION_CHANGED_EVENT,
} from "../pages/backend_url";
import { normalizeSourceContext, safeConnectionId } from "./connectionContext";

export const ACCOUNT_CONNECTION_CONTEXT_PATH = "/api/v1/account/connection-context";
// Selection is sent in a request header rather than a query string. This
// keeps the internal identifier out of browser history, referrers, proxies,
// analytics URLs, and copied support links.
export const ACCOUNT_CONNECTION_CONTEXT_HEADER = "X-CRM-Connection-Id";
const STORAGE_PREFIX = "crm.account-connection-scope.v1";

const AccountConnectionScopeContext = createContext(null);

const clean = (value) => String(value ?? "").trim();

const canonicalBoundaryValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(canonicalBoundaryValue)
      .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
  }
  if (!value || typeof value !== "object") return value;
  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = canonicalBoundaryValue(value[key]);
      return result;
    }, {});
};

/**
 * Internal identity/authority boundary used to invalidate protected in-memory
 * connection state. It is never rendered or placed in a URL.
 */
export function accountSessionBoundaryKey(user = getCachedSessionUser()) {
  const tenantId = clean(user?.tenantId);
  if (!tenantId) return "";
  const authorityFingerprint = JSON.stringify(canonicalBoundaryValue({
    access: user?.access ?? null,
    permissions: user?.permissions ?? null,
    dataScopes: user?.dataScopes ?? null,
    role: user?.role ?? null,
    roles: user?.roles ?? null,
  }));
  return [tenantId, clean(user?.userId), authorityFingerprint].join("\n");
}

function sessionBoundaryFromCachedSession() {
  const user = getCachedSessionUser();
  return {
    tenantId: clean(user?.tenantId),
    userId: clean(user?.userId),
    key: accountSessionBoundaryKey(user),
  };
}

/**
 * A connection preference belongs to one routed module, not to the whole
 * browser session.  Normalizing the pathname keeps route casing/trailing
 * slashes from creating accidental aliases while avoiding any customer or
 * connection details in the key.
 */
export function accountConnectionModuleKey(pathname) {
  const normalized = clean(pathname)
    .replace(/\/{2,}/g, "/")
    .replace(/\/$/, "");
  if (!normalized) return "/";
  return `${normalized.startsWith("/") ? "" : "/"}${normalized}`.toLowerCase();
}

const isCancelledRequest = (error, signal) =>
  signal?.aborted ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "AbortError" ||
  error?.name === "CanceledError";

const isDeniedSelection = (error) =>
  Number(error?.response?.status) === 403 &&
  ["connection_scope_denied", "connection_access_denied"].includes(
    clean(error?.response?.data?.error?.code)
  );

function shouldUseNativeCrossOriginConnectionTransport() {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return false;
  try {
    const apiOrigin = new URL(baseURL || "/", window.location.origin).origin;
    return apiOrigin !== window.location.origin;
  } catch {
    return false;
  }
}

function connectionContextUrl() {
  return new URL(ACCOUNT_CONNECTION_CONTEXT_PATH, baseURL || window.location.origin).toString();
}

async function nativeConnectionContextGet(config) {
  const request = window.fetch(connectionContextUrl(), {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: config.headers,
    signal: config.signal,
  });
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      const error = new Error("Connection access verification timed out.");
      error.name = "TimeoutError";
      reject(error);
    }, config.timeout || 15000);
  });

  try {
    const response = await Promise.race([request, timeout]);
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(
        data?.error?.message || data?.message || "Connection access could not be verified."
      );
      error.response = { status: response.status, data };
      throw error;
    }
    return { data };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export function accountConnectionStorageKey(tenantId, modulePathname) {
  const normalizedTenant = clean(tenantId);
  const rawModule = clean(modulePathname);
  const normalizedModule = accountConnectionModuleKey(modulePathname);
  return normalizedTenant && rawModule && normalizedModule
    ? `${STORAGE_PREFIX}:${encodeURIComponent(normalizedTenant)}:${encodeURIComponent(normalizedModule)}`
    : "";
}

function readStoredSelection(tenantId, modulePathname) {
  const key = accountConnectionStorageKey(tenantId, modulePathname);
  if (!key || typeof window === "undefined") return "";

  try {
    const stored = window.sessionStorage.getItem(key);
    const normalized = safeConnectionId(stored);
    if (stored && !normalized) window.sessionStorage.removeItem(key);
    return normalized;
  } catch {
    return "";
  }
}

function persistVerifiedSelection(tenantId, modulePathname, connectionId) {
  const key = accountConnectionStorageKey(tenantId, modulePathname);
  if (!key || typeof window === "undefined") return;

  try {
    const normalized = safeConnectionId(connectionId);
    if (normalized) window.sessionStorage.setItem(key, normalized);
    else window.sessionStorage.removeItem(key);
  } catch {
    // A restricted browser storage policy must not block protected CRM pages.
  }
}

/**
 * Stage a normalized connection as an untrusted navigation preference for a
 * different module. The destination provider still re-authorizes it with the
 * server before exposing protected data, and the identifier never enters the
 * browser URL.
 */
export function stageAccountConnectionSelection(tenantId, modulePathname, connectionId) {
  const normalized = safeConnectionId(connectionId);
  if (!clean(tenantId) || !clean(modulePathname) || !normalized) return false;
  persistVerifiedSelection(tenantId, modulePathname, normalized);
  return true;
}

function responseContext(response) {
  return normalizeSourceContext(response?.data?.meta);
}

function contextConfirmsSelection(context, requestedConnectionId) {
  const requested = safeConnectionId(requestedConnectionId);
  if (!context) return false;

  if (!requested) return context.mode === "all_permitted";
  return (
    context.mode === "single" &&
    context.selected_connection_id === requested &&
    context.options.some((option) => option.id === requested)
  );
}

/**
 * Holds a server-verified connection view for the authenticated account.
 *
 * A legacy URL value and the module-scoped sessionStorage value are only
 * navigation preferences. Each candidate is checked by the account
 * connection-context endpoint before it becomes the authoritative
 * `selectedConnectionId` exposed by this provider.
 */
export function AccountConnectionScopeProvider({
  children,
  enabled = true,
  required = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const moduleKey = accountConnectionModuleKey(location.pathname);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const latestLocationRef = useRef(location);
  latestLocationRef.current = location;

  const [sessionBoundary, setSessionBoundary] = useState(sessionBoundaryFromCachedSession);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const tenantId = sessionBoundary.tenantId;
  const [requestedConnectionId, setRequestedConnectionId] = useState("");
  const requestedRef = useRef("");
  const selectionGuardRef = useRef(null);
  const [resolution, setResolution] = useState({
    scopeKey: "",
    status: "idle",
    context: null,
    error: null,
  });
  const [refreshNumber, setRefreshNumber] = useState(0);
  const [selectionInitializedScope, setSelectionInitializedScope] = useState("");
  const initializedScopeRef = useRef("");
  const lastResolvedKeyRef = useRef("");
  const activeScopeKey = sessionBoundary.key && enabled
    ? `${sessionBoundary.key}\n${sessionEpoch}\n${moduleKey}`
    : "";

  const removeConnectionFromCurrentUrl = useCallback(() => {
    const current = latestLocationRef.current;
    const params = new URLSearchParams(current.search);
    if (!params.has("connection_id")) return;
    params.delete("connection_id");

    const nextSearchValue = params.toString();
    const nextSearch = nextSearchValue ? `?${nextSearchValue}` : "";
    if (nextSearch === current.search) return;

    navigateRef.current(
      { pathname: current.pathname, search: nextSearch, hash: current.hash },
      { replace: true }
    );
  }, []);

  const requestSelection = useCallback((connectionId) => {
    const normalized = safeConnectionId(connectionId);
    requestedRef.current = normalized;
    setRequestedConnectionId(normalized);
  }, []);

  const requestGuardedSelection = useCallback((connectionId) => {
    const normalized = safeConnectionId(connectionId);
    if (normalized === requestedRef.current) return;
    const proceed = () => requestSelection(normalized);
    const handled = selectionGuardRef.current?.({
      connectionId: normalized,
      proceed,
    });
    if (handled === true) return;
    proceed();
  }, [requestSelection]);

  const registerSelectionGuard = useCallback((guard) => {
    if (typeof guard !== "function") return () => {};
    selectionGuardRef.current = guard;
    return () => {
      if (selectionGuardRef.current === guard) selectionGuardRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleSessionChange = (event) => {
      const nextBoundary = event?.detail?.authenticated === false
        ? { tenantId: "", userId: "", key: "" }
        : sessionBoundaryFromCachedSession();
      // Every authoritative session event advances the epoch. A same-tenant
      // user or role change must never reuse the previous principal's resolved
      // connection context while a fresh server check is pending.
      setSessionBoundary(nextBoundary);
      setSessionEpoch((current) => current + 1);
    };

    window.addEventListener(SESSION_CHANGED_EVENT, handleSessionChange);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, handleSessionChange);
  }, []);

  // Accept a legacy query value only as an untrusted one-time candidate, then
  // immediately remove it. Customer-facing module URLs never carry internal
  // connection identifiers; each module keeps its own verified session-only
  // preference instead.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasLegacyConnection = params.has("connection_id");
    const rawConnectionId = clean(params.get("connection_id"));
    const legacyConnectionId = safeConnectionId(rawConnectionId);

    if (hasLegacyConnection) removeConnectionFromCurrentUrl();

    if (!tenantId) {
      initializedScopeRef.current = "";
      setSelectionInitializedScope("");
      lastResolvedKeyRef.current = "";
      requestedRef.current = "";
      setRequestedConnectionId("");
      return;
    }

    const initializationKey = [
      activeScopeKey,
      enabled ? "enabled" : "disabled",
      required ? "required" : "optional",
    ].join("\n");
    const scopeChanged = initializedScopeRef.current !== initializationKey;

    if (scopeChanged) {
      initializedScopeRef.current = initializationKey;
      lastResolvedKeyRef.current = "";
      const initialSelection = enabled
        ? (hasLegacyConnection
          ? legacyConnectionId
          : readStoredSelection(tenantId, moduleKey))
        : "";
      if (enabled && hasLegacyConnection && !legacyConnectionId) {
        // Empty or malformed URL candidates explicitly fail closed for this
        // module and cannot revive a previously stored selection.
        persistVerifiedSelection(tenantId, moduleKey, "");
      }
      requestSelection(initialSelection);
      setSelectionInitializedScope(enabled ? activeScopeKey : "");
      return;
    }

    if (!enabled) {
      // Connection-governance, voice, and legacy-integration routes have no email
      // selection. Their URL is still scrubbed above without changing the
      // saved preference of any email-aware module.
      return;
    }

    if (hasLegacyConnection && !legacyConnectionId) {
      persistVerifiedSelection(tenantId, moduleKey, "");
      requestSelection("");
      return;
    }

    if (hasLegacyConnection && legacyConnectionId !== requestedRef.current) {
      requestSelection(legacyConnectionId);
    }
  }, [
    location.pathname,
    location.search,
    activeScopeKey,
    enabled,
    moduleKey,
    required,
    removeConnectionFromCurrentUrl,
    requestSelection,
    tenantId,
  ]);

  useEffect(() => {
    if (!activeScopeKey || selectionInitializedScope !== activeScopeKey || !enabled) {
      if (!enabled) lastResolvedKeyRef.current = "";
      setResolution({ scopeKey: "", status: "idle", context: null, error: null });
      return undefined;
    }

    const controller = new AbortController();
    const requested = safeConnectionId(requestedConnectionId);
    const requestKey = `${activeScopeKey}\n${requested}\n${refreshNumber}`;
    if (lastResolvedKeyRef.current === requestKey) return undefined;
    let active = true;

    const fetchContext = async (connectionId = "") => {
      const config = {
        headers: { "Cache-Control": "no-store" },
        signal: controller.signal,
        timeout: 15000,
      };
      if (connectionId) {
        config.headers[ACCOUNT_CONNECTION_CONTEXT_HEADER] = connectionId;
      }
      // The production frontend and API are separate origins. Use the browser
      // fetch transport for this one shared boundary check so a third-party
      // extension that monkey-patches XMLHttpRequest cannot prevent an
      // authorized connection choice from taking effect. The request remains
      // credentialed, server-authorized, no-store and fail-closed.
      if (shouldUseNativeCrossOriginConnectionTransport()) {
        return nativeConnectionContextGet(config);
      }
      return apiClient.get(ACCOUNT_CONNECTION_CONTEXT_PATH, config);
    };

    const resolveContext = async () => {
      setResolution({ scopeKey: activeScopeKey, status: "loading", context: null, error: null });

      try {
        let response;
        let effectiveRequested = requested;
        try {
          response = await fetchContext(requested);
        } catch (error) {
          if (!requested || !isDeniedSelection(error)) throw error;

          // The selection may belong to an old account or a revoked agent
          // assignment. Clear it without revealing which condition occurred.
          effectiveRequested = "";
          response = await fetchContext();
        }

        if (!active || controller.signal.aborted) return;
        let context = responseContext(response);
        if (!contextConfirmsSelection(context, effectiveRequested) && effectiveRequested) {
          // A malformed or transitional response must not turn the browser
          // candidate into effective scope. Re-resolve the safe combined view.
          effectiveRequested = "";
          response = await fetchContext();
          if (!active || controller.signal.aborted) return;
          context = responseContext(response);
        }
        if (!contextConfirmsSelection(context, effectiveRequested)) {
          throw new Error("The server did not confirm the requested connection view.");
        }

        if (effectiveRequested !== requested) {
          requestedRef.current = "";
          setRequestedConnectionId("");
        }
        persistVerifiedSelection(tenantId, moduleKey, effectiveRequested);
        lastResolvedKeyRef.current = `${activeScopeKey}\n${effectiveRequested}\n${refreshNumber}`;
        setResolution({ scopeKey: activeScopeKey, status: "ready", context, error: null });
      } catch (error) {
        if (!active || isCancelledRequest(error, controller.signal)) return;
        setResolution({ scopeKey: activeScopeKey, status: "error", context: null, error });
      }
    };

    resolveContext();
    return () => {
      active = false;
      controller.abort();
    };
  }, [
    activeScopeKey,
    refreshNumber,
    enabled,
    moduleKey,
    required,
    requestedConnectionId,
    selectionInitializedScope,
    tenantId,
  ]);

  const visibleResolution = activeScopeKey && resolution.scopeKey === activeScopeKey
    ? resolution
    : {
      scopeKey: activeScopeKey,
      status: activeScopeKey ? "loading" : "idle",
      context: null,
      error: null,
    };
  const visibleRequestedConnectionId =
    activeScopeKey && selectionInitializedScope === activeScopeKey
      ? requestedConnectionId
      : "";

  const value = useMemo(() => {
    const ready = visibleResolution.status === "ready";
    const selectedConnectionId = ready
      ? safeConnectionId(visibleResolution.context?.selected_connection_id)
      : "";
    const selectionRequired = Boolean(required && ready && !selectedConnectionId);
    return {
      tenantId,
      sessionBoundaryKey: sessionBoundary.key,
      sessionEpoch,
      required: Boolean(required),
      status: visibleResolution.status,
      context: visibleResolution.context,
      options: ready ? visibleResolution.context?.options || [] : [],
      requestedConnectionId: visibleRequestedConnectionId,
      // Only the normalized server response can populate this value.
      selectedConnectionId,
      selectionRequired,
      operationalReady: Boolean(ready && (!required || selectedConnectionId)),
      error: visibleResolution.error,
      setSelectedConnectionId: requestGuardedSelection,
      registerSelectionGuard,
      refresh: () => setRefreshNumber((current) => current + 1),
    };
  }, [
    registerSelectionGuard,
    requestGuardedSelection,
    required,
    sessionBoundary.key,
    sessionEpoch,
    tenantId,
    visibleRequestedConnectionId,
    visibleResolution.context,
    visibleResolution.error,
    visibleResolution.status,
  ]);

  return (
    <AccountConnectionScopeContext.Provider value={value}>
      {children}
    </AccountConnectionScopeContext.Provider>
  );
}

export function useAccountConnectionScope() {
  const context = useContext(AccountConnectionScopeContext);
  if (!context) {
    throw new Error("useAccountConnectionScope must be used inside AccountConnectionScopeProvider.");
  }
  return context;
}

/**
 * Optional variant for reusable page widgets that also render in isolated
 * tests or non-routed previews. Production protected routes always provide the
 * shared account connection context.
 */
export function useOptionalAccountConnectionScope() {
  return useContext(AccountConnectionScopeContext);
}
